import { useState, useEffect, useCallback } from "react"
import { Search, Loader2, Plus } from "lucide-react"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import hive, { type TickerLookupResult, type Company } from "@/lib/api/hive"
import { useAuth } from "@/context/auth/auth-context"

interface ExternalLookupDialogProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	onTickerAdded: (ticker: TickerLookupResult, company?: Company) => void
	initialQuery?: string
}

export function ExternalLookupDialog({
	open,
	onOpenChange,
	onTickerAdded,
	initialQuery = "",
}: ExternalLookupDialogProps) {
	const { token } = useAuth()
	const [query, setQuery] = useState(initialQuery)
	const [results, setResults] = useState<TickerLookupResult[]>([])
	const [isLoading, setIsLoading] = useState(false)
	const [isAdding, setIsAdding] = useState<string | null>(null)
	const [error, setError] = useState<string | null>(null)

	// Reset query when dialog opens; clear results when it closes
	useEffect(() => {
		if (open) {
			setQuery(initialQuery)
		} else {
			setResults([])
		}
	}, [open, initialQuery])

	const handleSearch = useCallback(async (searchQuery: string) => {
		if (!searchQuery.trim()) return
		setIsLoading(true)
		setError(null)
		try {
			const data = await hive.lookupTickers(searchQuery)
			setResults(data)
		} catch (err) {
			setError("Failed to fetch results from external provider.")
			console.error(err)
		} finally {
			setIsLoading(false)
		}
	}, [])

	// Debounce search-as-you-type (300 ms); also fires on open when initialQuery is set
	useEffect(() => {
		if (!open || !query.trim()) {
			if (open) setResults([])
			return
		}
		const timer = setTimeout(() => handleSearch(query), 300)
		return () => clearTimeout(timer)
	}, [query, open, handleSearch])

	const handleAddTicker = async (ticker: TickerLookupResult) => {
		const id = `${ticker.symbol}-${ticker.exchange}`
		setIsAdding(id)
		setError(null)
		try {
			if (token) {
				// Authenticated: persist ticker and sync company data synchronously.
				// Pass name + currency as fallback for tickers the quote endpoint doesn't cover.
				const company = await hive.addTicker(
					ticker.symbol,
					ticker.exchange,
					token,
					ticker.name,
					ticker.currency,
				)
				onTickerAdded(ticker, company)
			} else {
				// Demo mode: skip API call, pass ticker with no company data
				onTickerAdded(ticker)
			}
			onOpenChange(false)
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to add ticker to database.")
			console.error(err)
		} finally {
			setIsAdding(null)
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-[500px]">
				<DialogHeader>
					<DialogTitle>Find your company</DialogTitle>
					<DialogDescription>
						We couldn't find your company in our database. Search external data to add it.
					</DialogDescription>
				</DialogHeader>
				<div className="space-y-4 py-4">
					<div className="space-y-2">
						<Label htmlFor="external-search">Company Name or Ticker</Label>
						<div className="flex gap-2">
							<Input
								id="external-search"
								value={query}
								onChange={(e) => setQuery(e.target.value)}
								placeholder="e.g. Apple or AAPL"
								onKeyDown={(e) => e.key === "Enter" && handleSearch(query)}
							/>
							<Button onClick={() => handleSearch(query)} disabled={isLoading}>
								{isLoading ? (
									<Loader2 className="h-4 w-4 animate-spin" />
								) : (
									<Search className="h-4 w-4" />
								)}
							</Button>
						</div>
					</div>

					{error && <p className="text-sm text-red-500">{error}</p>}

					<div className="space-y-2">
						{results.length > 0 ? (
							<div className="border rounded-md divide-y overflow-hidden">
								{results.map((result) => (
									<div
										key={`${result.symbol}-${result.exchange}`}
										className="flex items-center justify-between p-3 hover:bg-muted/50 transition-colors"
									>
										<div className="flex flex-col">
											<span className="font-medium text-sm">{result.name}</span>
											<span className="text-xs text-muted-foreground">
												{result.symbol} • {result.exchange} • {result.currency}
											</span>
										</div>
										<Button
											size="sm"
											variant="outline"
											onClick={() => handleAddTicker(result)}
											disabled={!!isAdding}
											className="border-sky-200 hover:bg-sky-50 hover:text-sky-700 dark:border-sky-900/50 dark:hover:bg-sky-900/20 dark:hover:text-sky-100"
										>
											{isAdding === `${result.symbol}-${result.exchange}` ? (
												<>
													<Loader2 className="h-4 w-4 animate-spin mr-1" />
													Adding...
												</>
											) : (
												<>
													<Plus className="h-4 w-4 mr-1" />
													Select
												</>
											)}
										</Button>
									</div>
								))}
							</div>
						) : (
							!isLoading &&
							query.length >= 2 && (
								<p className="text-center py-4 text-sm text-muted-foreground">
									No results found externally.
								</p>
							)
						)}
					</div>
				</div>
			</DialogContent>
		</Dialog>
	)
}
